/**
 * Ryan O'Dowd
 * 2022-12-19
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  store,
} from '@utils/store';

export default class Passage { // @TODO: throw error if attribute/method doesn't exist
  id: number;
  translationId: number;
  bookId: number;
  chapterNumber: number;
  startVerseNumber: number;
  endVerseNumber: number;
  rangeStartVerseNumber: number;
  rangeEndVerseNumber: number;

  translation_id?: number; // @TODO: yeet
  book_id?: number; // @TODO: yeet
  chapter_number?: number; // @TODO: yeet
  start_verse_number?: number; // @TODO: yeet
  end_verse_number?: number; // @TODO: yeet

  constructor(id, translationId, bookId, chapterNumber, startVerseNumber, endVerseNumber) { // @TODO: can there be another constructor sofr passing in a passage object (from the back end)?
    this.id = +id;
    this.translationId = +translationId;
    this.bookId = +bookId;
    this.chapterNumber = chapterNumber && +chapterNumber;
    this.startVerseNumber = startVerseNumber && +startVerseNumber;
    this.endVerseNumber = startVerseNumber && +endVerseNumber;
    this.rangeStartVerseNumber = startVerseNumber && +startVerseNumber;
    this.rangeEndVerseNumber = startVerseNumber && +endVerseNumber;

    this.translation_id = +translationId; // @TODO: better casing fix than this duplication
    this.book_id = +bookId; // @TODO: better casing fix than this duplication
    this.chapter_number = chapterNumber && +chapterNumber; // @TODO: better casing fix than this duplication
    this.start_verse_number = startVerseNumber && +startVerseNumber; // @TODO: better casing fix than this duplication
    this.end_verse_number = startVerseNumber && +endVerseNumber; // @TODO: better casing fix than this duplication

    const globalStore = store.getState();

    for (let i = this.startVerseNumber; i <= this.endVerseNumber; i++) {
      const verse = globalStore.verses?.[this.translationId]?.books?.[this.bookId]?.chapters?.[this.chapterNumber]?.verses?.[i];
      if (verse?.rangeStartVerseNumber) {
        this.rangeStartVerseNumber = Math.min(verse.rangeStartVerseNumber, this.rangeStartVerseNumber);
      }
      if (verse?.rangeEndVerseNumber) {
        this.rangeEndVerseNumber = Math.max(verse.rangeEndVerseNumber, this.rangeEndVerseNumber);
      }
    }
  }

  static createFromObject(p) {
    // useful for transforming passages sent from the back end into this javascript class object
    return new Passage(
      p.id,
      p.translation_id || p.translationId,
      p.book_id || p.bookId,
      p.chapter_number || p.chapterNumber,
      p.start_verse_number || p.startVerseNumber,
      p.end_verse_number || p.endVerseNumber,
    );
  }

  clone() {
    return new Passage(
      this.id,
      this.translationId,
      this.bookId,
      this.chapterNumber,
      this.startVerseNumber,
      this.endVerseNumber,
    );
  }

  cloneWithChanges(changes) {
    return new Passage(
      changes.id || this.id,
      changes.translationId || changes.translation_id || this.translationId,
      changes.bookId || changes.book_id || this.bookId,
      changes.chapterNumber || changes.chapter_number || this.chapterNumber,
      changes.startVerseNumber || changes.start_verse_number || this.startVerseNumber,
      changes.endVerseNumber || changes.end_verse_number || this.endVerseNumber,
    );
  }

  prettyPassageName(includeAbbreviation = false) { // @TODO: make sure this doesn't display `Infinity` for start or end verses (issue in passage selection on web) // @TODO: fix before prod
    const verses = !this.rangeStartVerseNumber ? '' : `${this.rangeStartVerseNumber}${(this.rangeEndVerseNumber && this.rangeStartVerseNumber !== this.rangeEndVerseNumber) ? `-${this.rangeEndVerseNumber}` : ''}`;
    return `${Passage.getBookName(this.translationId, this.bookId)}${this.chapterNumber ? ` ${this.chapterNumber}` : ''}${verses ? `:${verses}` : ''}${includeAbbreviation ? `, ${this.getTranslation()?.abbreviation || ''}` : ''}`;
  }

  getTranslation() {
    return Passage.getTranslation(this.translationId);
  }

  abbreviatedPassageName() {
    return `${Passage.getBookName(this.translationId, this.bookId).replace(' ', '').slice(0, 3)} ${this.chapterNumber}:${this.startVerseNumber}${this.startVerseNumber !== this.endVerseNumber ? `-${this.endVerseNumber}` : ''}`;
  }

  getBook() {
    return Passage.getBookById(this.bookId);
  }

  getChapter() {
    return Passage.getChapterByBookIdAndNumber(this.bookId, this.chapterNumber);
  }

  isValid() { // @TODO: should set error message in here so user knows why it's invalid
    // @TODO: check to make sure the reference exists (too high of a chapter/verse number)
    // @TODO: start_verse_number should be before or equal to end_verse_number
    // @TODO: amke sure this isn't an empty verse (e.g. Matthew 18:11)
    return this.translationId && this.bookId && this.chapterNumber && this.startVerseNumber && this.endVerseNumber;
  }

  getWrinkles() {
    /* @TODO: can this be a hook so that it's reactive?
     * see note in VerseMemory.js: `const _w = useTypedSelector((state) => state.wrinkles); // @TODO: this is hacky...only listening to this to trigger a re-render of this component when wrinkles change (because the passage prop doesn't change, there's no re-render after successfully leveling up a verse...can't repro on simulator)`
     */
    const passageWrinkles = [];
    const globalStore = store.getState();
    const book = globalStore.books.find((b) => b.id === this.book_id);
    for (let verseNum = this.start_verse_number; verseNum <= this.end_verse_number; verseNum++) {
      const translationWrinkle = (globalStore.wrinkles[Passage.getVerseApiId(book.api_id, this.chapter_number, verseNum)]?.filter((w) => w.translation_id === this.translation_id) || []).pop();
      if (translationWrinkle) {
        passageWrinkles.push(translationWrinkle);
      }
    }
    return passageWrinkles;
  }

  toApiNotation() {
    return `${this.getBook().api_id}.${this.chapterNumber}.${this.startVerseNumber}${this.startVerseNumber !== this.endVerseNumber ? `-${this.endVerseNumber}` : ''}.${this.getTranslation().abbreviation}`;
  }

  static getBookName(translationId, bookId) {
    return store.getState().bookNames[translationId]?.[bookId]?.name || ''; // @TODO: better default value
  }

  static getTranslation(translationId) {
    return store.getState().translations.find((t) => t.id === translationId);
  }

  static getBookById(bookId) {
    return store.getState().books.find((b) => b.id === bookId);
  }

  static getBookFromApiId(bookApiId) {
    return store.getState().books.find((b) => b.api_id === bookApiId);
  }

  static getBookFromChapterId(chapterId) {
    return store.getState().books.find((b) => b.id === store.getState().chapters.find((c) => c.id === chapterId)?.book_id);
  }

  static getChapterById(chapterId) {
    return store.getState().chapters.find((c) => c.id === chapterId);
  }

  static getChapterByBookIdAndNumber(bookId, chapterNumber) {
    return store.getState().chapters.find((c) => c.book_id === bookId && c.chapter_number === chapterNumber);
  }

  static getVerseApiId(bookApiId, chapterNumber, verseNumber) {
    return `${bookApiId}.${chapterNumber}.${verseNumber}`;
  }

  static createPassageFromReference(refereceString, translationId) { // @TODO: combine with utilities.getReferenceMatches
    const regex = /^([\dA-Za-z\s]+?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/;
    const match = refereceString.match(regex);

    if (!match) {
      return null;
    }

    const [, bookName, chapterNumberStr, startVerseStr, endVerseStr] = match;
    let bookNameFinal = bookName.trim();
    if (bookNameFinal === 'Psalms') { // @TODO: this block should be more dynamic
      bookNameFinal = 'Psalm';
    } else if (bookNameFinal === 'Song of Solomon') {
      bookNameFinal = 'Song of Songs';
    }

    let bookId;
    for (const [bookIdCandidate, bookNameCandidate] of Object.entries(store.getState().bookNames[translationId]) as any) {
      if (bookNameCandidate.name === bookNameFinal) {
        bookId = +bookIdCandidate;
        break;
      }
    }

    return new Passage(
      -1,
      translationId,
      bookId, // @TODO: may need error handling if book not found
      parseInt(chapterNumberStr, 10),
      startVerseStr ? parseInt(startVerseStr, 10) : 1,
      endVerseStr ? parseInt(endVerseStr, 10) : +Passage.getChapterByBookIdAndNumber(bookId, +chapterNumberStr).number_of_verses,
    );
  }
}
